import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  FileText,
  Plus,
  ShoppingCart,
  DollarSign,
  Flame,
  Trophy,
  Newspaper,
  HelpCircle,
  Users,
  CreditCard,
  BarChart3,
  X,
  LogOut,
  ChevronRight,
  MessageSquare,
  Settings,
  Receipt,
  UserPlus,
  ClipboardList,
  FolderOpen,
  Shield,
  Building2,
  Activity,
  SlidersHorizontal
} from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';

const commercialLinks = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Tableau de bord' },
  { to: '/declare-sale', icon: Plus, label: 'Declarer une vente' },
  { to: '/my-sales', icon: ShoppingCart, label: 'Mes ventes' },
  { to: '/commissions', icon: DollarSign, label: 'Mes commissions' },
  { to: '/invoices', icon: Receipt, label: 'Mes factures' },
  { to: '/docu-client', icon: FolderOpen, label: 'Docu-Client' },
  { to: '/attestations', icon: Shield, label: 'Attestations' },
  { to: '/leads-hot', icon: Flame, label: 'Leads Hot' },
  { to: '/documents', icon: FileText, label: 'Documents' },
  { to: '/messages', icon: MessageSquare, label: 'Messagerie' },
  { to: '/animation', icon: Trophy, label: 'Animation' },
  { to: '/news', icon: Newspaper, label: 'Actualites' },
  { to: '/settings', icon: Settings, label: 'Parametres' },
  { to: '/support', icon: HelpCircle, label: 'Support' },
];

const rhLinks = [
  { to: '/rh/dashboard', icon: LayoutDashboard, label: 'Tableau de bord' },
  { to: '/rh/commercials', icon: Users, label: 'Mes commerciaux' },
  { to: '/rh/fdc', icon: UserPlus, label: 'Declarer un contrat' },
  { to: '/rh/mes-factures', icon: Receipt, label: 'Mes factures' },
  { to: '/rh/docu-ac', icon: FolderOpen, label: 'Docu AC' },
  { to: '/attestations', icon: Shield, label: 'Attestations' },
  { to: '/documents', icon: FileText, label: 'Documents' },
  { to: '/messages', icon: MessageSquare, label: 'Messagerie' },
  { to: '/news', icon: Newspaper, label: 'Actualites' },
  { to: '/settings', icon: Settings, label: 'Parametres' },
  { to: '/support', icon: HelpCircle, label: 'Support' },
];

const adminLinks = [
  { to: '/admin/users', icon: Users, label: 'Utilisateurs' },
  { to: '/admin/fdc', icon: ClipboardList, label: 'Declarations RH' },
  { to: '/admin/attestations', icon: Shield, label: 'Attestations' },
  { to: '/admin/sales', icon: ShoppingCart, label: 'Toutes les ventes' },
  { to: '/admin/payments', icon: CreditCard, label: 'Paiements AC' },
  { to: '/admin/rh-payments', icon: CreditCard, label: 'Paiements RH' },
  { to: '/admin/docu-client', icon: FolderOpen, label: 'Demandes documents' },
  { to: '/admin/documents', icon: FileText, label: 'Gerer documents' },
  { to: '/admin/animation', icon: Trophy, label: 'Animation' },
  { to: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
  { to: '/messages', icon: MessageSquare, label: 'Messagerie' },
];

// SA Groupe : navigation minimaliste (uniquement Filiales + Analytics + Users transverses + Settings)
const groupSALinks = [
  { to: '/admin/agencies', icon: Building2, label: 'Filiales' },
  { to: '/admin/fdv-builder', icon: FileText, label: 'Formulaires FDV' },
  { to: '/admin/analytics', icon: BarChart3, label: 'Analytics Groupe' },
  { to: '/admin/users', icon: Users, label: 'Tous les utilisateurs' },
  { to: '/admin/tcsu', icon: SlidersHorizontal, label: 'TCSU' },
  { to: '/admin/animation', icon: Trophy, label: 'Animation & Actualités' },
  { to: '/messages', icon: MessageSquare, label: 'Messagerie' },
  { to: '/admin/audit-logs', icon: Shield, label: 'Journal audit' },
  { to: '/admin/system-health', icon: Activity, label: 'Santé système' },
  { to: '/settings', icon: Settings, label: 'Parametres' },
];

const roleLabels = {
  sa: 'Super Admin',
  soa: 'Administrateur',
  admin: 'Administrateur',
  rh: 'RH',
  commercial: 'Commercial',
};

export const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout, isAdmin, isRH, role, agencyName, isGroupSA } = useAuth();
  const location = useLocation();
  const [tcsuCanView, setTcsuCanView] = useState(false);

  useEffect(() => {
    if (isAdmin && !isGroupSA) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tcsu/access`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => setTcsuCanView(!!(d && d.can_view)))
        .catch(() => setTcsuCanView(false));
    }
  }, [isAdmin, isGroupSA]);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  // Determine which links to show based on role
  const mainLinks = isGroupSA ? [] : (isRH ? rhLinks : commercialLinks);
  const showAdminSection = isAdmin;
  let linksForAdminSection = isGroupSA ? groupSALinks : adminLinks;
  if (!isGroupSA && tcsuCanView && !linksForAdminSection.some((l) => l.to === '/admin/tcsu')) {
    linksForAdminSection = [...linksForAdminSection, { to: '/admin/tcsu', icon: SlidersHorizontal, label: 'TCSU' }];
  }

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={onClose}
          data-testid="sidebar-overlay"
        />
      )}
      
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-50 h-screen w-72 bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 flex flex-col transition-transform duration-300 lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid="sidebar"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <img 
              src="/logo-groupe-lnc.png" 
              alt="GROUPE-LNC Logo" 
              className="w-10 h-10 object-contain"
            />
            <div>
              <h1 className="font-bold text-xl text-slate-900 dark:text-white tracking-tight font-[Manrope]">
                GROUPE-LNC
              </h1>
              <p className="text-xs text-slate-500">
                {isRH ? 'Espace RH' : isAdmin ? 'Administration' : 'Espace partenaire'}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={onClose}
            data-testid="close-sidebar-btn"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-1">
            {mainLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    "sidebar-link group",
                    isActive && "active"
                  )
                }
                data-testid={`nav-${link.to.replace(/\//g, '-').slice(1)}`}
              >
                <link.icon className="w-5 h-5 transition-transform group-hover:scale-110" strokeWidth={1.5} />
                <span className="flex-1">{link.label}</span>
                <ChevronRight className="w-4 h-4 opacity-0 -translate-x-2 transition-all group-hover:opacity-50 group-hover:translate-x-0" />
              </NavLink>
            ))}

            {isAdmin && (
              <>
                {mainLinks.length > 0 && <Separator className="my-4" />}
                <p className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                  {isGroupSA ? 'Administration Groupe' : 'Administration'}
                </p>
                {linksForAdminSection.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    onClick={onClose}
                    className={({ isActive }) =>
                      cn(
                        "sidebar-link group",
                        isActive && "active"
                      )
                    }
                    data-testid={`nav-${link.to.replace(/\//g, '-').slice(1)}`}
                  >
                    <link.icon className="w-5 h-5 transition-transform group-hover:scale-110" strokeWidth={1.5} />
                    <span className="flex-1">{link.label}</span>
                    <ChevronRight className="w-4 h-4 opacity-0 -translate-x-2 transition-all group-hover:opacity-50 group-hover:translate-x-0" />
                  </NavLink>
                ))}
              </>
            )}
          </nav>
        </ScrollArea>

        {/* User section */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-900">
            <Avatar className="w-10 h-10 border-2 border-white shadow-sm">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-indigo-100 text-indigo-600 font-medium">
                {user?.name?.charAt(0)?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-slate-900 dark:text-white truncate">
                {user?.name}
              </p>
              <div className="flex items-center gap-1 flex-wrap">
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  {roleLabels[role] || role}
                </Badge>
                {isGroupSA ? (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200">
                    <Building2 className="w-2.5 h-2.5 mr-0.5" />
                    Groupe LNC
                  </Badge>
                ) : agencyName ? (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-indigo-50 text-indigo-700 border-indigo-200" data-testid="sidebar-agency-badge">
                    <Building2 className="w-2.5 h-2.5 mr-0.5" />
                    {agencyName}
                  </Badge>
                ) : null}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-slate-400 hover:text-rose-500"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};
